/**
 * Public tag page showing essays with a specific tag.
 * 
 * Features:
 * - Tag header
 * - List of essays with the tag
 * - Link back to all tags
 * 
 * Called by:
 * - App.tsx as /tag/:slug route
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Typography, Spin, Alert, Card, Tag, Space, Row, Col, Pagination, Breadcrumb } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, HomeOutlined, TagOutlined } from '@ant-design/icons';
import { getPublicEssays, getPublicTags, type PublicEssayListItem } from '../../services/api';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 12;

/**
 * Essay card component.
 */
function EssayCard({ essay }: { essay: PublicEssayListItem }) {
  return (
    <Link to={`/essays/${essay.slug}`} style={{ textDecoration: 'none' }}>
      <Card
        hoverable
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          height: '100%',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '24px' }}
        className="essay-card"
      >
        {essay.category && (
          <Tag
            style={{
              background: 'rgba(212, 175, 55, 0.15)',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              color: '#d4af37',
              marginBottom: '12px',
            }}
          >
            {essay.category.name}
          </Tag>
        )}

        <Title
          level={4}
          style={{
            color: '#f5f5f5',
            marginBottom: '8px',
            fontFamily: "var(--font-serif)",
          }}
        >
          {essay.title}
        </Title>

        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{
            color: '#a0a0a0',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            marginBottom: '16px',
          }}
        >
          {essay.preview}
        </Paragraph>

        <Space size="middle" style={{ color: '#666' }}>
          {essay.publishedAt && (
            <span>
              <CalendarOutlined style={{ marginRight: '4px' }} />
              {format(new Date(essay.publishedAt), 'MMM d, yyyy')}
            </span>
          )}
          <span>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {essay.readingTime} min read
          </span>
        </Space>
      </Card>
    </Link>
  );
}

/**
 * Public tag page component.
 */
function PublicTagPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  // Fetch essays with tag
  const {
    data: essaysData,
    isLoading: essaysLoading,
    error: essaysError,
  } = useQuery({
    queryKey: ['public', 'essays', 'tag', slug, page],
    queryFn: () => getPublicEssays({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      tag: slug,
    }),
    enabled: !!slug,
  });

  // Fetch tag info
  const { data: tags } = useQuery({
    queryKey: ['public', 'tags'],
    queryFn: getPublicTags,
  });

  const tag = tags?.find((t) => t.slug === slug);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Loading state
  if (essaysLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (essaysError) {
    return (
      <Alert
        message="Tag not found"
        description="The tag you're looking for doesn't exist."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: 'Tags' },
          { title: tag?.name || slug },
        ]}
      />

      {/* Tag header */}
      <div style={{ marginBottom: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <TagOutlined style={{ fontSize: '1.5rem', color: '#d4af37' }} />
          <Title
            style={{
              fontSize: '2.5rem',
              fontFamily: "var(--font-serif)",
              fontWeight: 400,
              color: '#f5f5f5',
              margin: 0,
            }}
          >
            #{tag?.name || slug}
          </Title>
        </div>
        <Text type="secondary">
          {essaysData?.total || 0} {essaysData?.total === 1 ? 'essay' : 'essays'}
        </Text>
      </div>

      {/* Empty state */}
      {essaysData && essaysData.essays.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">No essays with this tag yet.</Text>
        </div>
      )}

      {/* Essay grid */}
      {essaysData && essaysData.essays.length > 0 && (
        <>
          <Row gutter={[24, 24]}>
            {essaysData.essays.map((essay) => (
              <Col key={essay.id} xs={24} md={12} lg={8}>
                <EssayCard essay={essay} />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {essaysData.total > PAGE_SIZE && (
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Pagination
                current={page}
                total={essaysData.total}
                pageSize={PAGE_SIZE}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}

      <style>{`
        .essay-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicTagPage;
